/**
 * pages/BuyingCycle.tsx
 *
 * Buying Cycle — cohort analysis, lead timing, and sales cycle quality.
 * Under Sales Analytics in the sidebar.
 *
 * Data: real leads from /api/buying-cycle/leads (Application + EOC join).
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area,
  ComposedChart, Line, ReferenceLine, LabelList,
} from "recharts";
import { LeadHistoryCohortSection } from "./ads/metaLeadGen/components/LeadHistoryCohortSection";
import { useLeadHistoryAnalytics, type BuyingCycleFilters } from "../crmFunnel/leadHistory/useLeadHistoryAnalytics";
import type { LeadRecord } from "../crmFunnel/leadHistory/types";
import { branding } from "../config/branding";

interface FilterOptions {
  sources: string[];
  funnels: string[];
  contents: string[];
  countries: string[];
  closers: string[];
  setters: string[];
  responseTypes: string[];
  leadTypes: string[];
}

const YEAR_OPTIONS = [
  { label: "All Time", start: "", end: "" },
  { label: "2024", start: "2024-01-01", end: "2024-12-31" },
  { label: "2025", start: "2025-01-01", end: "2025-12-31" },
  { label: "2026", start: "2026-01-01", end: "2026-12-31" },
];

function FilterSelect({
  label, value, options, onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-0.5 min-w-[130px]">
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]"
      >
        <option value="">All</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

// ─────────────────────────────────────────────
// OPTIN LOOKUP TYPES
// ─────────────────────────────────────────────
interface OptinCall {
  callId: string;
  date: string;
  outcome: string;
  closer: string;
  setter: string;
  cash: number;
  offer: string;
  notes: string;
  fathomLink: string;
  sourcePlatform: string;
  sourceFunnel: string;
  country: string;
}

interface OptinProfile {
  leadId: string;
  name: string;
  email: string;
  phone: string;
  country: string;
  optinDate: string;
  allOptinDates: string[];
  applicationDate: string;
  speedToLeadDays: number | null;
  closingCallsCount: number;
  lastStatus: string | null;
  leadType: string;
  responseType: string;
  source: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent: string;
  utmTerm: string;
  hasBrand: string;
  currentMRR: string;
  ableToInvest: string;
  stage: string;
  isClosedWon: boolean;
  totalRevenue: number;
  calls: OptinCall[];
}

const CLOSING_OUTCOMES = new Set(["Closed", "Deposit Collected", "Downsell", "Downsold", "Upsell", "MRR"]);

function stageBadge(stage: string, isClosedWon: boolean) {
  if (isClosedWon) return "bg-green-500/15 text-green-400 border-green-500/30";
  if (stage === "Call Taken") return "bg-blue-500/15 text-blue-400 border-blue-500/30";
  if (stage === "Lost") return "bg-red-500/15 text-red-400 border-red-500/30";
  return "bg-muted/40 text-muted-foreground border-border";
}

function fmtDate(d: string) {
  if (!d) return "—";
  const dt = new Date(d + "T12:00:00Z");
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtCash(n: number) {
  if (!n) return "—";
  return `$${n.toLocaleString()}`;
}

function InfoRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-muted-foreground min-w-[110px] shrink-0">{label}</span>
      <span className="text-foreground font-medium break-all">{value}</span>
    </div>
  );
}

function OptinLookup() {
  const [query, setQuery]               = useState("");
  const [results, setResults]           = useState<OptinProfile[]>([]);
  const [loading, setLoading]           = useState(false);
  const [selected, setSelected]         = useState<OptinProfile | null>(null);
  const [expandedCall, setExpandedCall] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSelected(null); return; }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    fetch(`/api/buying-cycle/optin-lookup?q=${encodeURIComponent(q)}`, { signal: ctrl.signal })
      .then(r => r.json() as Promise<{ results: OptinProfile[] }>)
      .then(d => { setResults(d.results ?? []); setLoading(false); })
      .catch(err => { if ((err as Error).name !== "AbortError") setLoading(false); });
    return () => ctrl.abort();
  }, [query]);

  return (
    <div className="space-y-4">
      {/* Search input */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search by name or email…"
          value={query}
          onChange={e => { setQuery(e.target.value); setSelected(null); }}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)]/40"
        />
        {loading && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground animate-pulse">Searching…</span>
        )}
      </div>

      <div className="flex gap-4 items-start">
        {/* Results list */}
        {results.length > 0 && (
          <div className="w-72 shrink-0 rounded-xl border border-border bg-card divide-y divide-border overflow-hidden">
            {results.map(r => (
              <button
                key={r.leadId}
                onClick={() => { setSelected(r); setExpandedCall(null); }}
                className={`w-full text-left px-3 py-2.5 hover:bg-muted/20 transition-colors ${selected?.leadId === r.leadId ? "bg-[var(--brand-primary)]/8" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-foreground truncate">{r.name || r.email}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 ${stageBadge(r.stage, r.isClosedWon)}`}>{r.stage}</span>
                </div>
                <div className="text-[10px] text-muted-foreground truncate mt-0.5">{r.email}</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-muted-foreground">{fmtDate(r.optinDate)}</span>
                  {r.totalRevenue > 0 && <span className="text-[10px] text-green-400 font-semibold">{fmtCash(r.totalRevenue)}</span>}
                  {r.calls.length > 0 && <span className="text-[10px] text-muted-foreground">{r.calls.length} call{r.calls.length !== 1 ? "s" : ""}</span>}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Profile panel */}
        {selected && (
          <div className="flex-1 rounded-xl border border-border bg-card p-5 space-y-5 min-w-0">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-foreground">{selected.name || selected.email}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{selected.email}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {selected.totalRevenue > 0 && (
                  <span className="text-sm font-bold text-green-400">{fmtCash(selected.totalRevenue)}</span>
                )}
                <span className={`text-[11px] px-2 py-0.5 rounded-full border font-semibold ${stageBadge(selected.stage, selected.isClosedWon)}`}>{selected.stage}</span>
              </div>
            </div>

            {/* Attribution */}
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border pb-1">Attribution</p>
              <InfoRow label="Optin date"    value={fmtDate(selected.optinDate)} />
              <InfoRow label="Source"        value={selected.source} />
              <InfoRow label="UTM Source"    value={selected.utmSource} />
              <InfoRow label="UTM Medium"    value={selected.utmMedium} />
              <InfoRow label="UTM Campaign"  value={selected.utmCampaign} />
              <InfoRow label="UTM Content"   value={selected.utmContent} />
              <InfoRow label="UTM Term"      value={selected.utmTerm} />
              <InfoRow label="Country"       value={selected.country} />
              <InfoRow label="Phone"         value={selected.phone} />
              <InfoRow label="Lead type"     value={selected.leadType} />
              <InfoRow label="Response type" value={selected.responseType} />
            </div>

            {/* Application answers */}
            {(selected.hasBrand || selected.currentMRR || selected.ableToInvest) && (
              <div className="space-y-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border pb-1">Application</p>
                <InfoRow label="Has brand"     value={selected.hasBrand} />
                <InfoRow label="Current MRR"   value={selected.currentMRR} />
                <InfoRow label="Able to invest" value={selected.ableToInvest} />
              </div>
            )}

            {/* Calls */}
            {selected.calls.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-b border-border pb-1">
                  Calls ({selected.calls.length})
                </p>
                <div className="space-y-2">
                  {selected.calls.map((call, i) => {
                    const isClose = CLOSING_OUTCOMES.has(call.outcome);
                    const isExpanded = expandedCall === call.callId;
                    return (
                      <div key={call.callId} className={`rounded-lg border transition-colors ${isClose ? "border-green-500/25 bg-green-500/5" : "border-border bg-muted/10"}`}>
                        <button
                          onClick={() => setExpandedCall(isExpanded ? null : call.callId)}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-[10px] font-mono text-muted-foreground shrink-0">#{i + 1}</span>
                            <span className="text-xs font-semibold text-foreground shrink-0">{fmtDate(call.date)}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 ${isClose ? "border-green-500/30 text-green-400 bg-green-500/10" : "border-border text-muted-foreground"}`}>
                              {call.outcome || "—"}
                            </span>
                            {call.cash > 0 && <span className="text-[11px] font-bold text-green-400 shrink-0">{fmtCash(call.cash)}</span>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 text-[10px] text-muted-foreground">
                            {call.closer && <span>{call.closer}</span>}
                            <svg className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-3 pb-3 space-y-1 border-t border-border/50 pt-2">
                            <InfoRow label="Closer"   value={call.closer} />
                            <InfoRow label="Setter"   value={call.setter} />
                            <InfoRow label="Offer"    value={call.offer} />
                            <InfoRow label="Platform" value={call.sourcePlatform} />
                            <InfoRow label="Funnel"   value={call.sourceFunnel} />
                            <InfoRow label="Country"  value={call.country} />
                            {call.notes && (
                              <div className="mt-2 pt-2 border-t border-border/50">
                                <p className="text-[10px] text-muted-foreground mb-1">Notes</p>
                                <p className="text-xs text-foreground leading-relaxed">{call.notes}</p>
                              </div>
                            )}
                            {call.fathomLink && (
                              <a href={call.fathomLink} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 mt-2 text-[11px] text-[var(--brand-primary)] hover:underline">
                                View Fathom recording ↗
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {selected.calls.length === 0 && (
              <p className="text-xs text-muted-foreground italic">No calls on record for this optin.</p>
            )}
          </div>
        )}
      </div>

      {query.trim().length >= 2 && !loading && results.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-8">No optins found for "{query}"</p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// OPTIN AGE CHART
// ─────────────────────────────────────────────
interface OptinAgeData {
  avgDays: number;
  total: number;
  distribution: Array<{ bucket: string; count: number }>;
}

interface OptinAgeBucket { bucket: string; count: number; pct: number; cumulativePct: number }

function OptinAgeChart({ filters = {} }: { filters?: BuyingCycleFilters }) {
  const [data, setData] = useState<OptinAgeData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.startDate) params.set("startDate", filters.startDate);
    if (filters.endDate)   params.set("endDate",   filters.endDate);
    if (filters.source)    params.set("source",    filters.source);
    const qs = params.toString() ? `?${params}` : "";
    fetch(`/api/buying-cycle/optin-age${qs}`)
      .then(r => r.json() as Promise<OptinAgeData>)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filters.startDate, filters.endDate, filters.source]);

  const avgYears = data ? (data.avgDays / 365).toFixed(1) : null;

  const enrichedDist = useMemo((): OptinAgeBucket[] => {
    if (!data) return [];
    const total = data.distribution.reduce((s, b) => s + b.count, 0);
    let running = 0;
    return data.distribution.map(b => {
      const pct = total > 0 ? (b.count / total) * 100 : 0;
      running += pct;
      return { ...b, pct, cumulativePct: Math.min(running, 100) };
    });
  }, [data]);

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Optin Age Distribution</h3>
          <p className="text-xs text-muted-foreground mt-0.5">How old are the current optin records in the pipeline</p>
        </div>
        {data && !loading && (
          <div className="text-right shrink-0">
            <div className="text-2xl font-black text-[var(--brand-primary)]">{data.avgDays}d</div>
            <div className="text-[10px] text-muted-foreground">{avgYears}y avg age · {data.total.toLocaleString()} optins</div>
          </div>
        )}
        {loading && <div className="text-xs text-muted-foreground animate-pulse">Loading…</div>}
      </div>

      {data && !loading && (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={enrichedDist} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip
              contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 8, fontSize: 12 }}
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const b = payload[0].payload as OptinAgeBucket;
                return (
                  <div className="rounded-xl border border-border/60 bg-card px-3 py-2 text-[11px] space-y-1" style={{ boxShadow: "0 4px 16px rgba(0,0,0,0.18)" }}>
                    <div className="font-semibold text-foreground">{b.bucket}</div>
                    <div className="flex justify-between gap-4"><span className="text-muted-foreground">Optins</span><span className="font-medium">{b.count.toLocaleString()}</span></div>
                    <div className="flex justify-between gap-4"><span className="text-muted-foreground">%</span><span className="font-medium">{b.pct.toFixed(1)}%</span></div>
                    <div className="flex justify-between gap-4 pt-1 border-t border-border/30"><span className="text-muted-foreground">Cumulative</span><span className="font-semibold" style={{ color: branding.colors.primary }}>{b.cumulativePct.toFixed(1)}%</span></div>
                  </div>
                );
              }}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]} fill={withAlpha(branding.colors.primary, 0.72)}>
              <LabelList dataKey="pct" position="top" fontSize={9} fill="var(--muted-foreground)"
                formatter={(v: number) => `${v.toFixed(0)}%`} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// LEAD TIMELINE PANEL
// ─────────────────────────────────────────────
type TimelineEvent =
  | { type: "optin"; date: string; isFirst: boolean }
  | { type: "application"; date: string }
  | { type: "call"; date: string; call: OptinCall };

function LeadTimelinePanel({ email, onClose }: { email: string; onClose: () => void }) {
  const [profile, setProfile] = useState<OptinProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setProfile(null);
    fetch(`/api/buying-cycle/optin-lookup?q=${encodeURIComponent(email)}`)
      .then(r => r.json() as Promise<{ results: OptinProfile[] }>)
      .then(d => { setProfile(d.results?.[0] ?? null); setLoading(false); })
      .catch(() => setLoading(false));
  }, [email]);

  const events: TimelineEvent[] = useMemo(() => {
    if (!profile) return [];
    const allDates = profile.allOptinDates?.length > 0 ? profile.allOptinDates : [profile.optinDate];
    const optinEvents: TimelineEvent[] = allDates
      .filter(Boolean)
      .map((d, i) => ({ type: "optin" as const, date: d, isFirst: i === 0 }));
    const appEvent: TimelineEvent[] = profile.applicationDate
      ? [{ type: "application" as const, date: profile.applicationDate }]
      : [];
    const callEvents: TimelineEvent[] = profile.calls.map(c => ({ type: "call" as const, date: c.date, call: c }));
    return [...optinEvents, ...appEvent, ...callEvents]
      .filter(e => e.date)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [profile]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md bg-card border-l border-border flex flex-col h-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-foreground truncate">{profile?.name || email}</h2>
            {profile?.name && <p className="text-xs text-muted-foreground truncate mt-0.5">{email}</p>}
          </div>
          <button onClick={onClose} className="ml-3 shrink-0 p-1.5 rounded-lg hover:bg-muted/30 text-muted-foreground">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {loading && (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground animate-pulse">Loading history…</div>
        )}
        {!loading && !profile && (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">No profile found</div>
        )}

        {profile && !loading && (
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
            {/* Scorecard chips */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${stageBadge(profile.stage, profile.isClosedWon)}`}>
                {profile.stage}
              </span>
              {profile.totalRevenue > 0 && (
                <span className="text-xs font-bold text-green-400 px-2.5 py-1 rounded-full border border-green-500/30 bg-green-500/10">
                  {fmtCash(profile.totalRevenue)} collected
                </span>
              )}
              {profile.closingCallsCount > 0 && (
                <span className="text-xs px-2.5 py-1 rounded-full border border-green-500/25 bg-green-500/5 text-green-400 font-semibold">
                  {profile.closingCallsCount} closing call{profile.closingCallsCount !== 1 ? "s" : ""}
                </span>
              )}
              {profile.speedToLeadDays != null && (
                <span className="text-xs px-2.5 py-1 rounded-full border border-border text-muted-foreground">
                  Speed to lead: {profile.speedToLeadDays}d
                </span>
              )}
              {profile.calls.length > 0 && (
                <span className="text-xs text-muted-foreground">{profile.calls.length} total call{profile.calls.length !== 1 ? "s" : ""}</span>
              )}
              {profile.lastStatus && (
                <span className="text-xs text-muted-foreground italic">Last: {profile.lastStatus}</span>
              )}
            </div>

            {/* Timeline */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-4">History Timeline</p>
              <div className="relative pl-9">
                <div className="absolute left-3 top-2 bottom-2 w-px bg-border" />
                <div className="space-y-6">
                  {events.map((ev, i) => (
                    <div key={i} className="relative">
                      <div className={`absolute -left-9 top-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        ev.type === "optin"
                          ? "bg-[var(--brand-primary)]/10 border-[var(--brand-primary)]"
                          : ev.type === "application"
                            ? "bg-blue-500/10 border-blue-400"
                            : CLOSING_OUTCOMES.has(ev.call.outcome)
                              ? "bg-green-500/10 border-green-500"
                              : "bg-muted/20 border-border"
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          ev.type === "optin"
                            ? "bg-[var(--brand-primary)]"
                            : ev.type === "application"
                              ? "bg-blue-400"
                              : CLOSING_OUTCOMES.has(ev.call.outcome) ? "bg-green-400" : "bg-muted-foreground/50"
                        }`} />
                      </div>
                      <div>
                        {ev.type === "optin" ? (
                          <>
                            <div className="text-xs font-semibold text-foreground">
                              {ev.isFirst ? "First Optin" : "Re-optin"}
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(ev.date)}</div>
                          </>
                        ) : ev.type === "application" ? (
                          <>
                            <div className="text-xs font-semibold text-blue-400">Applied</div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">{fmtDate(ev.date)}</div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold text-foreground">{fmtDate(ev.call.date)}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${
                                CLOSING_OUTCOMES.has(ev.call.outcome)
                                  ? "border-green-500/30 text-green-400 bg-green-500/10"
                                  : "border-border text-muted-foreground"
                              }`}>
                                {ev.call.outcome || "Unknown"}
                              </span>
                              {ev.call.cash > 0 && (
                                <span className="text-[11px] font-bold text-green-400">{fmtCash(ev.call.cash)}</span>
                              )}
                            </div>
                            <div className="flex gap-3 mt-0.5 text-[10px] text-muted-foreground flex-wrap">
                              {ev.call.closer && <span>Closer: {ev.call.closer}</span>}
                              {ev.call.setter && <span>Setter: {ev.call.setter}</span>}
                            </div>
                            {ev.call.notes && (
                              <p className="mt-1 text-[10px] text-muted-foreground italic line-clamp-3">{ev.call.notes}</p>
                            )}
                            {ev.call.fathomLink && (
                              <a href={ev.call.fathomLink} target="_blank" rel="noopener noreferrer"
                                className="mt-0.5 block text-[10px] text-[var(--brand-primary)] hover:underline">
                                View recording ↗
                              </a>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  {events.length === 0 && (
                    <p className="text-xs text-muted-foreground">No events on record</p>
                  )}
                </div>
              </div>
            </div>

            {/* Attribution */}
            <div className="border-t border-border pt-4 space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Attribution</p>
              <InfoRow label="First optin"   value={fmtDate(profile.optinDate)} />
              <InfoRow label="Applied"       value={fmtDate(profile.applicationDate)} />
              <InfoRow label="Source"        value={profile.source} />
              <InfoRow label="UTM Campaign"  value={profile.utmCampaign} />
              <InfoRow label="UTM Content"   value={profile.utmContent} />
              <InfoRow label="Country"       value={profile.country} />
              <InfoRow label="Lead type"     value={profile.leadType} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// ALL LEADS TABLE
// ─────────────────────────────────────────────
function AllLeadsSection({ filters }: { filters: BuyingCycleFilters }) {
  const [leads, setLeads]               = useState<LeadRecord[]>([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState("");
  const [page, setPage]                 = useState(1);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const PAGE_SIZE = 25;

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.startDate) params.set("startDate", filters.startDate);
    if (filters.endDate)   params.set("endDate",   filters.endDate);
    fetch(`/api/buying-cycle/leads${params.toString() ? `?${params}` : ""}`)
      .then(r => r.json() as Promise<{ leads: LeadRecord[]; total: number }>)
      .then(d => { setLeads(d.leads ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filters.startDate, filters.endDate]);

  const filtered = useMemo(() => {
    let rows = leads;
    if (filters.source)  rows = rows.filter(r => r.source === filters.source || r.utmSource === filters.source);
    if (filters.country) rows = rows.filter(r => r.country === filters.country);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r => r.email.toLowerCase().includes(q));
    }
    return rows;
  }, [leads, filters.source, filters.country, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-foreground">All Leads</h3>
          <p className="text-xs text-muted-foreground">
            {loading ? "Loading…" : `${filtered.length.toLocaleString()} optins${search ? " matching search" : ""} · click any row to view timeline`}
          </p>
        </div>
        <input
          type="text"
          placeholder="Search by email…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="h-8 w-52 rounded-lg border border-border bg-background px-3 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/10">
              <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Lead</th>
              <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground">Source</th>
              <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground">Country</th>
              <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground">App Date</th>
              <th className="text-left px-3 py-2.5 font-semibold text-muted-foreground">Stage</th>
              <th className="text-right px-3 py-2.5 font-semibold text-muted-foreground">Speed to Lead</th>
              <th className="text-right px-4 py-2.5 font-semibold text-muted-foreground">Cash Collected</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="text-center py-16 text-muted-foreground animate-pulse">Loading leads…</td>
              </tr>
            )}
            {!loading && paged.map(lead => {
              const speedDays = (lead.optinDate && lead.leadCreatedAt)
                ? Math.max(0, Math.round((new Date(lead.leadCreatedAt).getTime() - new Date(lead.optinDate).getTime()) / 86_400_000))
                : null;
              return (
                <tr
                  key={lead.leadId}
                  onClick={() => setSelectedEmail(lead.email)}
                  className="border-b border-border/40 hover:bg-[var(--brand-primary)]/5 cursor-pointer transition-colors group"
                >
                  <td className="px-4 py-2.5 max-w-[220px]">
                    <span className="font-medium text-foreground group-hover:text-[var(--brand-primary)] transition-colors truncate block">{lead.email}</span>
                    <span className="text-[10px] text-muted-foreground truncate block">{fmtDate(lead.optinDate ?? lead.leadCreatedAt)}</span>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">{lead.source || "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{lead.country || "—"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">{fmtDate(lead.leadCreatedAt)}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${stageBadge(lead.currentStage, lead.isClosedWon)}`}>
                      {lead.currentStage}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground">
                    {speedDays != null ? `${speedDays}d` : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right font-semibold">
                    {lead.revenue > 0
                      ? <span className="text-green-400">{fmtCash(lead.revenue)}</span>
                      : <span className="text-muted-foreground/30">—</span>}
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-16 text-muted-foreground">No leads found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-border flex-wrap gap-2">
          <span className="text-xs text-muted-foreground">{filtered.length} total · page {page} of {totalPages}</span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-40 hover:bg-muted/20 transition-colors"
            >←</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce<(number | "…")[]>((acc, p, i, arr) => {
                if (i > 0 && (arr[i - 1] as number) < p - 1) acc.push("…");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === "…"
                  ? <span key={`ellipsis-${i}`} className="px-1 text-xs text-muted-foreground">…</span>
                  : <button
                      key={p}
                      onClick={() => setPage(p as number)}
                      className={`w-7 h-7 rounded-md text-xs font-medium transition-colors ${
                        page === p
                          ? "bg-[var(--brand-primary)] text-white"
                          : "border border-border hover:bg-muted/20 text-muted-foreground"
                      }`}
                    >{p}</button>
              )
            }
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-2.5 py-1 rounded-md border border-border text-xs disabled:opacity-40 hover:bg-muted/20 transition-colors"
            >→</button>
          </div>
        </div>
      )}

      {selectedEmail && (
        <LeadTimelinePanel email={selectedEmail} onClose={() => setSelectedEmail(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// OPTIN → APPLICATION DISTRIBUTION CHART
// ─────────────────────────────────────────────

interface AppDailyPoint {
  dayLabel: string;
  day: number;
  count: number;
  pct: number;
  isOutlier: boolean;
}
interface AppDistKPIs {
  median: number; mean: number; p80: number;
  pct7d: number; pct30d: number; maxDays: number; totalApps: number;
}
interface AppDistData {
  daily: AppDailyPoint[];
  kpis: AppDistKPIs;
  sources: string[];
  campaigns: string[];
  offers: string[];
  medianDay: number;
  meanDay: number;
  p80Day: number;
  insight: string;
}

interface AppDayBarProps {
  x?: number; y?: number; width?: number; height?: number;
  payload?: AppDailyPoint;
}
function AppDayBar({ x = 0, y = 0, width = 0, height = 0, payload }: AppDayBarProps) {
  if (height <= 0 || width <= 0) return null;
  const sec      = branding.colors.secondary;
  const isOutlier = payload?.isOutlier ?? false;
  const rc = Math.min(2, width / 2, height);
  const path = `M${x},${y + height} L${x},${y + rc} Q${x},${y} ${x + rc},${y} L${x + width - rc},${y} Q${x + width},${y} ${x + width},${y + rc} L${x + width},${y + height} Z`;
  const gradId = `adbar-${payload?.day ?? 0}`;
  const color  = isOutlier ? branding.colors.accent : sec;
  return (
    <g>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity={isOutlier ? 0.55 : 0.90} />
          <stop offset="100%" stopColor={color} stopOpacity={isOutlier ? 0.20 : 0.32} />
        </linearGradient>
      </defs>
      <path d={path} fill={`url(#${gradId})`} />
    </g>
  );
}

interface AppDailyPointEnriched extends AppDailyPoint {
  cumulativePct: number;
}

interface AppDayTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: AppDailyPointEnriched }>;
  total: number;
}
function AppDayTooltip({ active, payload }: AppDayTooltipProps) {
  if (!active || !payload?.[0]) return null;
  const b   = payload[0].payload;
  const sec = branding.colors.secondary;
  return (
    <div className="rounded-2xl border border-border/40 bg-card/96 backdrop-blur-lg px-4 py-3 min-w-[170px]"
      style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-[12px] font-bold text-foreground">
          {b.isOutlier ? "30+ days" : `Day ${b.day}`}
        </span>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
          style={{ background: withAlpha(sec, 0.12), color: sec }}>
          {b.pct.toFixed(1)}%
        </span>
      </div>
      <div className="space-y-1 text-[11px]">
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Applications</span>
          <span className="font-semibold text-foreground tabular-nums">{b.count.toLocaleString()}</span>
        </div>
        {b.cumulativePct != null && (
          <div className="flex justify-between gap-4 pt-1 border-t border-border/30 mt-1">
            <span className="text-muted-foreground">Cumulative</span>
            <span className="font-semibold tabular-nums" style={{ color: sec }}>{b.cumulativePct.toFixed(1)}%</span>
          </div>
        )}
        {b.isOutlier && (
          <p className="text-[10px] text-muted-foreground/70 pt-1 border-t border-border/40 mt-1">
            Long-tail (30+ days since optin)
          </p>
        )}
      </div>
    </div>
  );
}

function OptinApplicationDistributionChart({ filters = {} }: { filters?: BuyingCycleFilters }) {
  const [dist,      setDist]      = useState<AppDistData | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [excludeFT, setExcludeFT] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.source)    params.set("source",     filters.source);
    if (filters.startDate) params.set("optinStart", filters.startDate);
    if (filters.endDate)   params.set("optinEnd",   filters.endDate);
    if (filters.funnel)    params.set("campaign",   filters.funnel);
    if (excludeFT)         params.set("excludeFT",  "1");
    const qs = params.toString() ? `?${params}` : "";
    fetch(`/api/buying-cycle/application-distribution${qs}`)
      .then(r => r.json() as Promise<AppDistData>)
      .then(d => { setDist(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filters.source, filters.startDate, filters.endDate, filters.funnel, excludeFT]);

  const sec = branding.colors.secondary;

  const { kpis, daily = [], medianDay = 0 } = dist ?? {};

  // Group: days 0–29 individual, everything ≥30 collapses to "30+"
  const groupedDaily = useMemo((): AppDailyPointEnriched[] => {
    if (!daily.length) return [];
    const under30 = daily.filter(d => d.day < 30 && !d.isOutlier);
    const over30   = daily.filter(d => d.day >= 30 || d.isOutlier);
    const over30Count = over30.reduce((s, d) => s + d.count, 0);
    const totalAppsLocal = daily.reduce((s, d) => s + d.count, 0);
    const over30Pct = totalAppsLocal > 0 ? (over30Count / totalAppsLocal) * 100 : 0;
    const rows = [...under30];
    if (over30Count > 0) rows.push({ dayLabel: "30+", day: 30, count: over30Count, pct: over30Pct, isOutlier: true });
    let running = 0;
    return rows.map(r => { running += r.pct; return { ...r, cumulativePct: Math.min(running, 100) }; });
  }, [daily]);

  const kpiCards = kpis ? [
    { label: "Median Days",    value: `${kpis.median}d`,  sub: "real behavior",   highlight: true },
    { label: "Average Days",   value: `${kpis.mean}d`,    sub: "arithmetic mean", highlight: false },
    { label: "Apply ≤ 7 Days", value: `${kpis.pct7d}%`,  sub: "fast-apply rate", highlight: false },
  ] : [];

  const totalApps = kpis?.totalApps ?? 0;

  // X-axis ticks: 0, 5, 10, 15, 20, 25, 30+
  const xTicks = useMemo(() => {
    const ticks: string[] = [];
    for (let d = 0; d < 30; d += 5) ticks.push(String(d));
    ticks.push("30+");
    return ticks;
  }, []);

  const medianLabel = String(Math.min(medianDay, 29));

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: 0.08 }}
      className="rounded-2xl border border-border bg-card overflow-hidden"
      style={{ boxShadow: "0 2px 28px rgba(0,0,0,0.06)" }}
    >
      {/* ── Header ── */}
      <div className="px-6 pt-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-[13px] font-semibold text-foreground tracking-tight">
              Optin → Application Distribution
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Day-by-day: how many optins booked their first call on each day after opting in
            </p>
          </div>
        </div>


        {/* ── KPI Strip ── */}
        <div className="grid grid-cols-3 sm:grid-cols-3 gap-2.5 mt-4">
          {kpiCards.map((k, i) => (
            <div key={i} className="rounded-xl px-3 py-2.5 border border-border/50"
              style={{ background: withAlpha(sec, k.highlight ? 0.06 : 0.02) }}>
              <div className="text-[9px] text-muted-foreground font-medium uppercase tracking-wider leading-tight mb-1">{k.label}</div>
              <div className={`tabular-nums font-bold leading-none tracking-tight ${k.highlight ? "text-[18px]" : "text-[15px]"}`}
                style={{ color: k.highlight ? sec : "var(--foreground)" }}>
                {loading ? "—" : k.value}
              </div>
              <div className="text-[9px] text-muted-foreground/50 mt-1">{k.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Chart ── */}
      <div className="px-3 pt-5 pb-2">
        {loading ? (
          <div className="h-[340px] flex items-center justify-center">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: sec }} />
              Computing day-by-day distribution…
            </div>
          </div>
        ) : totalApps === 0 ? (
          <div className="h-[340px] flex items-center justify-center text-[13px] text-muted-foreground">
            No matched applications for the selected filters.
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={`${filters.source}-${filters.startDate}-${filters.endDate}-${filters.funnel}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}>
              <ResponsiveContainer width="100%" height={340}>
                <ComposedChart data={groupedDaily} margin={{ top: 12, right: 44, bottom: 20, left: 4 }}
                  barCategoryGap="8%">
                  <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.04} />
                  <XAxis
                    dataKey="dayLabel"
                    ticks={xTicks}
                    tick={{ fontSize: 9, fill: "var(--muted-foreground)", fontFamily: "inherit" }}
                    tickLine={false} axisLine={false}
                    label={{ value: "Days since optin", position: "insideBottom", offset: -12, fontSize: 9, fill: "var(--muted-foreground)" }}
                  />
                  <YAxis yAxisId="count"
                    tick={{ fontSize: 9, fill: "var(--muted-foreground)", fontFamily: "inherit" }}
                    tickLine={false} axisLine={false} width={28}
                    label={{ value: "Applications", angle: -90, position: "insideLeft", offset: 8, fontSize: 9, fill: "var(--muted-foreground)" }}
                  />
                  <YAxis yAxisId="cumPct" orientation="right"
                    domain={[0, 100]} tickFormatter={v => `${v}%`}
                    tick={{ fontSize: 9, fill: "var(--muted-foreground)", fontFamily: "inherit" }}
                    tickLine={false} axisLine={false} width={36} />
                  <Tooltip content={<AppDayTooltip total={totalApps} />}
                    cursor={{ fill: withAlpha(sec, 0.06) }} />

                  {/* 50% mark (median) */}
                  <>
                    <ReferenceLine yAxisId="count" x={medianLabel} strokeWidth={10} stroke={withAlpha(sec, 0.09)} />
                    <ReferenceLine yAxisId="count" x={medianLabel} stroke={withAlpha(sec, 0.85)} strokeWidth={1.5}
                      label={<RefLabel text={`50% · Day ${medianDay}`} color={sec} side="right" />} />
                  </>

                  {/* Bars */}
                  <Bar yAxisId="count" dataKey="count" maxBarSize={14}
                    shape={(props: unknown) => <AppDayBar {...(props as AppDayBarProps)} />}
                  />

                  {/* Cumulative % line */}
                  <Line yAxisId="cumPct" type="monotone" dataKey="cumulativePct"
                    stroke={withAlpha(sec, 0.60)}
                    strokeWidth={1.5} strokeDasharray="5 3"
                    dot={false} activeDot={{ r: 3, fill: sec, strokeWidth: 0 }}
                    style={{ pointerEvents: "none" }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* ── Legend ── */}
      <div className="flex items-center gap-5 px-6 pb-5 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ background: withAlpha(sec, 0.7) }} />
          <span className="text-[10px] text-muted-foreground">Applications per day</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm" style={{ background: withAlpha(branding.colors.accent, 0.45) }} />
          <span className="text-[10px] text-muted-foreground">30+ outliers</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-[2px] rounded-full" style={{ background: sec }} />
          <span className="text-[10px] text-muted-foreground">50% mark</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg width="16" height="2" viewBox="0 0 16 2">
            <line x1="0" y1="1" x2="16" y2="1" stroke={withAlpha(sec, 0.60)} strokeWidth="1.5" strokeDasharray="5 3" />
          </svg>
          <span className="text-[10px] text-muted-foreground">Cumulative %</span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <button
            onClick={() => setExcludeFT(v => !v)}
            className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[10px] font-medium border transition-all ${
              excludeFT
                ? "border-[var(--brand-secondary)] text-[var(--brand-secondary)] bg-[color-mix(in_srgb,var(--brand-secondary)_10%,transparent)]"
                : "border-border text-muted-foreground hover:border-[var(--brand-secondary)] hover:text-[var(--brand-secondary)]"
            }`}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="shrink-0">
              {excludeFT
                ? <path d="M2 5h6M5 2v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" transform="rotate(45 5 5)" />
                : <path d="M1.5 3h7M3 5h4M4.5 7h1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />}
            </svg>
            {excludeFT ? "FT excluded" : "Exclude Free Training"}
          </button>
          <span className="text-[10px] text-muted-foreground/40 tabular-nums">
            {totalApps > 0 ? `n = ${totalApps.toLocaleString()} applications` : ""}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────
// BUYING CYCLE DISTRIBUTION CHART
// ─────────────────────────────────────────────

interface DistBucket {
  label: string; min: number; max: number;
  count: number; pct: number;
  dominantSource: string; avgRevenue: number;
}
interface DistKPIs {
  median: number; mean: number; p25: number; p75: number; p80: number;
  pct14d: number; maxDays: number; totalCloses: number;
}
interface DistData {
  buckets: DistBucket[];
  kpis: DistKPIs;
  sources: string[];
  medianLabel: string | null;
  meanLabel: string | null;
  p80Label: string | null;
  insight: string;
}

function roundedTopPath(x: number, y: number, w: number, h: number, r: number): string {
  const rc = Math.min(r, w / 2, h);
  return `M${x},${y + h} L${x},${y + rc} Q${x},${y} ${x + rc},${y} L${x + w - rc},${y} Q${x + w},${y} ${x + w},${y + rc} L${x + w},${y + h} Z`;
}

interface DistBarProps {
  x?: number; y?: number; width?: number; height?: number;
  index?: number; fill?: string; hoveredIdx: number | null;
  barColors: string[];
}
function DistBar({ x = 0, y = 0, width = 0, height = 0, index = 0, hoveredIdx, barColors }: DistBarProps) {
  if (height <= 0) return null;
  const isActive  = hoveredIdx === null || hoveredIdx === index;
  const gradId    = `bdist-bar-${index}`;
  const color     = barColors[index] ?? barColors[0];
  return (
    <g style={{ opacity: isActive ? 1 : 0.18, transition: "opacity 0.22s ease" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity={1}    />
          <stop offset="100%" stopColor={color} stopOpacity={0.38} />
        </linearGradient>
      </defs>
      <path d={roundedTopPath(x, y, width, height, 8)} fill={`url(#${gradId})`} />
    </g>
  );
}

interface DistTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: DistBucket & { cumulativePct: number } }>;
  label?: string;
  total: number;
}
function DistTooltip({ active, payload, total }: DistTooltipProps) {
  if (!active || !payload?.[0]) return null;
  const b = payload[0].payload;
  return (
    <div className="rounded-2xl border border-border/40 bg-card/96 backdrop-blur-lg px-4 py-3.5 min-w-[192px]"
      style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)" }}>
      <div className="flex items-center justify-between gap-4 mb-2.5">
        <span className="text-[13px] font-bold text-foreground tabular-nums">{b.label}</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
          style={{ background: withAlpha(branding.colors.primary, 0.12), color: branding.colors.primary }}>
          {b.pct}%
        </span>
      </div>
      <div className="space-y-1.5">
        <div className="flex justify-between text-[11px]">
          <span className="text-muted-foreground">Closes</span>
          <span className="font-semibold text-foreground tabular-nums">{b.count.toLocaleString()}</span>
        </div>
        {b.avgRevenue > 0 && (
          <div className="flex justify-between text-[11px]">
            <span className="text-muted-foreground">Avg cash/lead</span>
            <span className="font-semibold text-foreground tabular-nums">${b.avgRevenue.toLocaleString()}</span>
          </div>
        )}
        {b.cumulativePct != null && (
          <div className="flex justify-between text-[11px] pt-1 border-t border-border/30 mt-1">
            <span className="text-muted-foreground">Cumulative</span>
            <span className="font-semibold tabular-nums" style={{ color: branding.colors.primary }}>{b.cumulativePct.toFixed(1)}%</span>
          </div>
        )}
      </div>
      <div className="mt-2.5 pt-2 border-t border-border/40 text-[10px] text-muted-foreground/60 text-center">
        {total > 0 ? `${total.toLocaleString()} total closes` : ""}
      </div>
    </div>
  );
}

interface RefLabelProps {
  viewBox?: { x?: number; y?: number; height?: number };
  text: string;
  color: string;
  side?: "left" | "right";
}
function RefLabel({ viewBox, text, color, side = "left" }: RefLabelProps) {
  if (!viewBox) return null;
  const vx = viewBox.x ?? 0;
  const vy = viewBox.y ?? 0;
  const xPos = side === "right" ? vx + 6 : vx - 4;
  const anchor = side === "right" ? "start" : "end";
  return (
    <g>
      <rect x={xPos - (side === "right" ? 0 : 60)} y={vy + 6}
        width={60} height={15} rx={4}
        fill={withAlpha(color, 0.12)} />
      <text x={xPos + (side === "right" ? 4 : -4)} y={vy + 17}
        fill={color} fontSize={9} fontWeight="700" textAnchor={anchor}
        fontFamily="inherit">
        {text}
      </text>
    </g>
  );
}

function BuyingCycleDistributionChart({ filters = {} }: { filters?: BuyingCycleFilters }) {
  const [dist,       setDist]       = useState<DistData | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.source)    params.set("source",    filters.source);
    if (filters.startDate) params.set("startDate", filters.startDate);
    if (filters.endDate)   params.set("endDate",   filters.endDate);
    const qs = params.toString() ? `?${params}` : "";
    fetch(`/api/buying-cycle/distribution${qs}`)
      .then(r => r.json() as Promise<DistData>)
      .then(d => { setDist(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filters.source, filters.startDate, filters.endDate]);

  const barColors = useMemo(() => {
    if (!dist) return [];
    const p   = branding.colors.primary;
    const n   = dist.buckets.length;
    return dist.buckets.map((_, i) => {
      const t     = 1 - i / Math.max(n - 1, 1);   // 1 → 0 left to right
      const alpha = 0.14 + t * 0.82;               // 0.96 → 0.14
      return withAlpha(p, alpha);
    });
  }, [dist]);

  const { kpis, buckets = [], medianLabel } = dist ?? {};

  // Enrich buckets with running cumulative %
  const enrichedBuckets = useMemo(() => {
    let running = 0;
    return buckets.map(b => {
      running += b.pct;
      return { ...b, cumulativePct: Math.min(running, 100) };
    });
  }, [buckets]);

  const kpiCards = kpis ? [
    { label: "Median Cycle",  value: `${kpis.median}d`,  sub: "real buying behavior" },
    { label: "Average Cycle", value: `${kpis.mean}d`,    sub: "arithmetic mean" },
    { label: "Fastest 25%",   value: `≤ ${kpis.p25}d`,  sub: "Q1 quartile" },
    { label: "Slowest 25%",   value: `≥ ${kpis.p75}d`,  sub: "Q3 quartile" },
  ] : [];

  const totalCloses = kpis?.totalCloses ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="rounded-2xl border border-border bg-card overflow-hidden"
      style={{ boxShadow: "0 2px 28px rgba(0,0,0,0.06)" }}
    >
      {/* ── Header ── */}
      <div className="px-6 pt-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-[13px] font-semibold text-foreground tracking-tight">Buying Cycle Distribution</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">Application → Close · How long does it actually take leads to close?</p>
          </div>
        </div>

        {/* ── KPI Strip ── */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2.5 mt-4">
          {kpiCards.map((k, i) => (
            <div key={i} className="rounded-xl px-3 py-2.5 border border-border/50"
              style={{ background: withAlpha(branding.colors.primary, i === 0 ? 0.05 : 0.02) }}>
              <div className="text-[9px] text-muted-foreground font-medium uppercase tracking-wider leading-tight mb-1">{k.label}</div>
              <div className={`tabular-nums font-bold leading-none tracking-tight ${i === 0 ? "text-[18px]" : "text-[15px]"}`}
                style={{ color: i === 0 ? branding.colors.primary : "var(--foreground)" }}>
                {k.value}
              </div>
              <div className="text-[9px] text-muted-foreground/50 mt-1">{k.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Chart ── */}
      <div className="px-2 pt-5 pb-2">
        {loading ? (
          <div className="h-[300px] flex items-center justify-center">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: branding.colors.primary }} />
              Computing distribution…
            </div>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={`${filters.source}-${filters.startDate}-${filters.endDate}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={enrichedBuckets} margin={{ top: 8, right: 44, bottom: 0, left: 4 }}
                  onMouseLeave={() => setHoveredIdx(null)}>
                  <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.04} />
                  <XAxis dataKey="label"
                    tick={{ fontSize: 10, fill: "var(--muted-foreground)", fontFamily: "inherit" }}
                    tickLine={false} axisLine={false} />
                  <YAxis yAxisId="count"
                    tick={{ fontSize: 10, fill: "var(--muted-foreground)", fontFamily: "inherit" }}
                    tickLine={false} axisLine={false} width={32} />
                  <YAxis yAxisId="cumPct" orientation="right"
                    domain={[0, 100]} tickFormatter={v => `${v}%`}
                    tick={{ fontSize: 9, fill: "var(--muted-foreground)", fontFamily: "inherit" }}
                    tickLine={false} axisLine={false} width={36} />
                  <Tooltip
                    content={<DistTooltip total={totalCloses} />}
                    cursor={{ fill: withAlpha(branding.colors.primary, 0.05), radius: 8 }}
                  />

                  {/* Median reference line */}
                  {medianLabel && <>
                    <ReferenceLine yAxisId="count" x={medianLabel} strokeWidth={10}
                      stroke={withAlpha(branding.colors.primary, 0.09)} />
                    <ReferenceLine yAxisId="count" x={medianLabel}
                      stroke={withAlpha(branding.colors.primary, 0.80)} strokeWidth={1.5}
                      label={<RefLabel text={`Median ${kpis?.median ?? 0}d`} color={branding.colors.primary} side="right" />}
                    />
                  </>}

                  {/* Bars — custom shape with hover fade */}
                  <Bar yAxisId="count" dataKey="count" maxBarSize={72}
                    shape={(props: unknown) => {
                      const p = props as DistBarProps;
                      return <DistBar {...p} hoveredIdx={hoveredIdx} barColors={barColors} />;
                    }}
                    onMouseEnter={(_: unknown, index: number) => setHoveredIdx(index)}
                    onMouseLeave={() => setHoveredIdx(null)}
                  />

                  {/* Cumulative % line */}
                  <Line yAxisId="cumPct" type="monotone" dataKey="cumulativePct"
                    stroke={withAlpha(branding.colors.primary, 0.60)}
                    strokeWidth={1.5} strokeDasharray="5 3"
                    dot={false} activeDot={{ r: 3, fill: branding.colors.primary, strokeWidth: 0 }}
                    style={{ pointerEvents: "none" }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </motion.div>
          </AnimatePresence>
        )}
      </div>

      {/* ── Legend ── */}
      <div className="flex items-center gap-5 px-6 pb-5 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-1 rounded-full" style={{ background: `linear-gradient(to right, ${withAlpha(branding.colors.primary, 0.92)}, ${withAlpha(branding.colors.primary, 0.14)})` }} />
          <span className="text-[10px] text-muted-foreground">Short → long cycle</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-4 h-[2px] rounded-full" style={{ background: branding.colors.primary }} />
          <span className="text-[10px] text-muted-foreground">Median</span>
        </div>
        <div className="flex items-center gap-1.5">
          <svg width="16" height="2" viewBox="0 0 16 2">
            <line x1="0" y1="1" x2="16" y2="1" stroke={withAlpha(branding.colors.primary, 0.60)} strokeWidth="1.5" strokeDasharray="5 3" />
          </svg>
          <span className="text-[10px] text-muted-foreground">Cumulative %</span>
        </div>
        <div className="ml-auto text-[10px] text-muted-foreground/40 tabular-nums">
          {totalCloses > 0 ? `n = ${totalCloses.toLocaleString()} closes` : ""}
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────
// REVENUE ORIGIN COHORT CHART
// ─────────────────────────────────────────────

const COHORT_KEYS = ["91+d", "61-90d", "31-60d", "15-30d", "8-14d", "0-7d"] as const;
type CohortKey = typeof COHORT_KEYS[number];

const COHORT_LABELS: Record<CohortKey, string> = {
  "91+d":   "91+ days old",
  "61-90d": "61–90 days",
  "31-60d": "31–60 days",
  "15-30d": "15–30 days",
  "8-14d":  "8–14 days",
  "0-7d":   "0–7 days",
};

interface RevenueCohortPoint {
  period: string;
  periodKey: string;
  "0-7d": number;
  "8-14d": number;
  "15-30d": number;
  "31-60d": number;
  "61-90d": number;
  "91+d": number;
  total: number;
}

interface RevenueCohortKPIs {
  pctFromMatureCohorts: number;
  medianDaysToClose: number;
  oldestConvertingDays: number;
  fastestSource: string;
  fastestSourceDays: number;
  totalRevenue: number;
  insight: string;
}

// Monochromatic color scale derived from branding primary
// oldest cohort → most faded; newest → most vivid
function withAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function buildCohortColors(): Record<CohortKey, string> {
  const p = branding.colors.primary;
  return {
    "91+d":   withAlpha(p, 0.30),
    "61-90d": withAlpha(p, 0.46),
    "31-60d": withAlpha(p, 0.60),
    "15-30d": withAlpha(p, 0.74),
    "8-14d":  withAlpha(p, 0.87),
    "0-7d":   withAlpha(p, 1.00),
  };
}

function buildCohortStrokeColors(): Record<CohortKey, string> {
  const p = branding.colors.primary;
  return {
    "91+d":   withAlpha(p, 0.38),
    "61-90d": withAlpha(p, 0.54),
    "31-60d": withAlpha(p, 0.68),
    "15-30d": withAlpha(p, 0.82),
    "8-14d":  withAlpha(p, 0.92),
    "0-7d":   withAlpha(p, 1.00),
  };
}

function fmtRevenue(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

interface RevenueCohortTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; fill: string }>;
  label?: string;
  colors: Record<CohortKey, string>;
  isApplications?: boolean;
}

function RevenueCohortTooltip({ active, payload, label, colors, isApplications }: RevenueCohortTooltipProps) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value ?? 0), 0);
  const fmtVal = (v: number) => isApplications ? v.toLocaleString() : fmtRevenue(v);
  return (
    <div className="rounded-2xl border border-border/50 bg-card/95 backdrop-blur-md shadow-2xl px-4 py-3 min-w-[180px]"
      style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)" }}>
      <div className="text-[11px] font-semibold text-foreground mb-2.5">{label}</div>
      <div className="space-y-1.5">
        {[...payload].reverse().map(p => {
          if (!p.value) return null;
          const key = p.name as CohortKey;
          const pct = total > 0 ? ((p.value / total) * 100).toFixed(0) : "0";
          return (
            <div key={p.name} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: colors[key] ?? p.fill }} />
                <span className="text-[10px] text-muted-foreground">{COHORT_LABELS[key] ?? p.name}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-muted-foreground/60">{pct}%</span>
                <span className="text-[11px] font-semibold text-foreground tabular-nums">{fmtVal(p.value)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2.5 pt-2 border-t border-border/40 flex justify-between items-center">
        <span className="text-[10px] text-muted-foreground">Total</span>
        <span className="text-[12px] font-bold text-foreground tabular-nums">{fmtVal(total)}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Optin → Application Funnel
// Dual bars (optins + apps) + ratio line, by month
// ─────────────────────────────────────────────────────────────────

interface OptinAppPoint { period: string; periodKey: string; optins: number; apps: number; ratio: number | null }
interface OptinAppKPIs  { totalOptins: number; totalApps: number; overallRatio: number; bestMonth: string | null; bestRatio: number | null; worstMonth: string | null; worstRatio: number | null }

function OptinAppFunnelChart({ filters = {} }: { filters?: BuyingCycleFilters }) {
  const [data,        setData]        = useState<OptinAppPoint[]>([]);
  const [kpis,        setKpis]        = useState<OptinAppKPIs | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [granularity, setGranularity] = useState<"month" | "week">("month");

  const pri = branding.colors.primary;
  const sec = branding.colors.secondary;
  const acc = branding.colors.accent;

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("granularity", granularity);
    if (filters.startDate) params.set("startDate", filters.startDate);
    if (filters.endDate)   params.set("endDate",   filters.endDate);
    if (filters.source)    params.set("source",    filters.source);
    fetch(`/api/buying-cycle/optin-app-funnel?${params}`)
      .then(r => r.json() as Promise<{ data: OptinAppPoint[]; kpis: OptinAppKPIs }>)
      .then(d => { setData(d.data ?? []); setKpis(d.kpis ?? null); setLoading(false); })
      .catch(() => setLoading(false));
  }, [granularity, filters.startDate, filters.endDate, filters.source]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="rounded-2xl border border-border bg-card overflow-hidden"
      style={{ boxShadow: "0 2px 24px rgba(0,0,0,0.06)" }}
    >
      {/* Header */}
      <div className="px-6 pt-6 pb-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-[13px] font-semibold text-foreground tracking-tight">Optin → Application Funnel</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              How many optins it takes to generate one application each month
            </p>
          </div>
          <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted/30 border border-border/50">
            {(["month", "week"] as const).map(g => (
              <button key={g} onClick={() => setGranularity(g)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all duration-200 ${
                  granularity === g ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}>
                {g === "month" ? "Monthly" : "Weekly"}
              </button>
            ))}
          </div>
        </div>

        {/* KPI strip */}
        {kpis && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {[
              { label: "Total Optins",       value: kpis.totalOptins.toLocaleString(),    sub: "filtered period"       },
              { label: "Total Applications", value: kpis.totalApps.toLocaleString(),      sub: "unique per email"      },
              { label: "Overall Ratio",      value: `${kpis.overallRatio}:1`,             sub: "optins per application"},
              { label: "Best Month",         value: kpis.bestMonth ?? "—",                sub: kpis.bestRatio != null ? `${kpis.bestRatio}:1 ratio` : "n/a" },
            ].map(kpi => (
              <div key={kpi.label} className="rounded-xl px-4 py-3 border border-border/60"
                style={{ background: withAlpha(pri, 0.03) }}>
                <div className="text-[10px] text-muted-foreground mb-1">{kpi.label}</div>
                <div className="text-[18px] font-bold text-foreground tabular-nums leading-none">{kpi.value}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{kpi.sub}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="px-2 pt-4 pb-2">
        {loading ? (
          <div className="flex items-center justify-center h-72 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={data} margin={{ top: 8, right: 48, left: 4, bottom: 0 }} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke={withAlpha(pri, 0.06)} vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} />
              {/* Left axis — counts */}
              <YAxis yAxisId="count" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} width={44} />
              {/* Right axis — ratio */}
              <YAxis yAxisId="ratio" orientation="right" tick={{ fontSize: 10, fill: withAlpha(acc, 0.8) }} axisLine={false} tickLine={false} width={36}
                tickFormatter={v => `${v}:1`} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const optins = payload.find(p => p.dataKey === "optins")?.value as number ?? 0;
                  const apps   = payload.find(p => p.dataKey === "apps")?.value as number ?? 0;
                  const ratio  = payload.find(p => p.dataKey === "ratio")?.value as number | null ?? null;
                  return (
                    <div className="rounded-xl border border-border/60 px-3 py-2.5 text-[11px]"
                      style={{ background: "var(--card)", boxShadow: "0 4px 20px rgba(0,0,0,0.18)", minWidth: 160 }}>
                      <div className="font-semibold text-foreground mb-2">{label}</div>
                      <div className="flex items-center justify-between gap-4 py-0.5">
                        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full" style={{ background: sec }} /><span className="text-muted-foreground">Optins</span></div>
                        <span className="font-medium tabular-nums">{optins.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-0.5">
                        <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full" style={{ background: pri }} /><span className="text-muted-foreground">Applications</span></div>
                        <span className="font-medium tabular-nums">{apps.toLocaleString()}</span>
                      </div>
                      {ratio !== null && (
                        <div className="flex items-center justify-between gap-4 py-0.5 mt-1 pt-1 border-t border-border/40">
                          <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full" style={{ background: acc }} /><span className="text-muted-foreground">Ratio</span></div>
                          <span className="font-medium tabular-nums">{ratio}:1</span>
                        </div>
                      )}
                    </div>
                  );
                }}
                cursor={{ fill: withAlpha(pri, 0.05) }}
              />
              <Bar yAxisId="count" dataKey="optins" name="Optins" fill={withAlpha(sec, 0.6)} stroke={withAlpha(sec, 0.8)} strokeWidth={0.5} radius={[3, 3, 0, 0]} />
              <Bar yAxisId="count" dataKey="apps"   name="Applications" fill={withAlpha(pri, 0.75)} stroke={withAlpha(pri, 0.9)} strokeWidth={0.5} radius={[3, 3, 0, 0]} />
              <Line yAxisId="ratio" dataKey="ratio" name="Ratio (optins/app)" type="monotone"
                stroke={acc} strokeWidth={2} dot={{ fill: acc, r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: acc }} connectNulls={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Legend */}
      <div className="px-6 pb-5 flex flex-wrap gap-x-5 gap-y-1.5">
        {[
          { color: sec, label: "Optins" },
          { color: pri, label: "Applications" },
          { color: acc, label: "Optins per Application (right axis)", dashed: false },
        ].map(item => (
          <div key={item.label} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: item.color }} />
            {item.label}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Cash Collection Cohort
// X-axis = payment month · Stacks = month the close was generated
// ─────────────────────────────────────────────────────────────────

interface CashCohortPoint  { period: string; periodKey: string; total: number; [cohort: string]: number | string }
interface CashCohortKPIs   { totalCash: number; pctCollectedSameMonth: number; longestTailMonths: number; avgLagMonths: number }
interface CohortMeta       { key: string; label: string }
interface CohortTableRow   { cohort: string; label: string; offsets: Record<number, number>; total: number }

// index 0 = most recent cohort → brightest chart color, older = progressively more faded
function getCashCohortColor(index: number, _total: number): string {
  const chart = branding.colors.chart;
  const base = chart[index % chart.length] ?? chart[0];
  if (index === 0) return base;
  const alpha = Math.max(0.30, 0.85 - index * 0.10);
  return withAlpha(base, alpha);
}

interface CashCohortTooltipProps { active?: boolean; payload?: { name: string; value: number; fill: string }[]; label?: string; cohorts: CohortMeta[] }
function CashCohortTooltip({ active, payload, label, cohorts }: CashCohortTooltipProps) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value ?? 0), 0);
  const fmt = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;
  const cohortLabelMap = Object.fromEntries(cohorts.map(c => [c.key, c.label]));
  return (
    <div className="rounded-xl border border-border/60 px-3 py-2.5 text-[11px]"
      style={{ background: "var(--card)", boxShadow: "0 4px 20px rgba(0,0,0,0.18)", minWidth: 170 }}>
      <div className="font-semibold text-foreground mb-1.5">{label} — {fmt(total)}</div>
      {[...payload].reverse().filter(p => p.value > 0).map(p => (
        <div key={p.name} className="flex items-center justify-between gap-4 py-0.5">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full shrink-0" style={{ background: p.fill }} />
            <span className="text-muted-foreground">{cohortLabelMap[p.name] ?? p.name} closes</span>
          </div>
          <span className="font-medium tabular-nums text-foreground">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function CashCollectionCohortChart({ filters = {} }: { filters?: BuyingCycleFilters }) {
  const [data,         setData]         = useState<CashCohortPoint[]>([]);
  const [cohorts,      setCohorts]      = useState<CohortMeta[]>([]);
  const [kpis,         setKpis]         = useState<CashCohortKPIs | null>(null);
  const [cohortTable,  setCohortTable]  = useState<CohortTableRow[]>([]);
  const [maxOffset,    setMaxOffset]    = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [granularity,  setGranularity]  = useState<"month" | "week">("month");
  const [hoveredCohort, setHoveredCohort] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("granularity", granularity);
    if (filters.startDate) params.set("startDate", filters.startDate);
    if (filters.endDate)   params.set("endDate",   filters.endDate);
    fetch(`/api/buying-cycle/cash-cohort?${params}`)
      .then(r => r.json() as Promise<{ data: CashCohortPoint[]; cohorts: CohortMeta[]; kpis: CashCohortKPIs; cohortTable: CohortTableRow[]; maxOffset: number }>)
      .then(d => {
        setData(d.data ?? []);
        setCohorts(d.cohorts ?? []);
        setKpis(d.kpis ?? null);
        setCohortTable(d.cohortTable ?? []);
        setMaxOffset(d.maxOffset ?? 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [granularity, filters.startDate, filters.endDate]);

  const fmt = (n: number) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${Math.round(n)}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="rounded-2xl border border-border bg-card overflow-hidden"
      style={{ boxShadow: "0 2px 24px rgba(0,0,0,0.06)" }}
    >
      {/* Header */}
      <div className="px-6 pt-6 pb-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-[13px] font-semibold text-foreground tracking-tight">Cash Collection Cohort</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Payment month × close cohort · see when cash from each close month actually lands
            </p>
          </div>
          <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted/30 border border-border/50">
            {(["month", "week"] as const).map(g => (
              <button key={g} onClick={() => setGranularity(g)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all duration-200 ${
                  granularity === g ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}>
                {g === "month" ? "Monthly" : "Weekly"}
              </button>
            ))}
          </div>
        </div>

        {/* KPI strip */}
        {kpis && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {[
              { label: "Total Cash",          value: fmt(kpis.totalCash),                       sub: "all cohorts"           },
              { label: "Collected Same Month", value: `${kpis.pctCollectedSameMonth}%`,          sub: "pay-in-full rate"      },
              { label: "Avg Lag",              value: `${kpis.avgLagMonths}mo`,                  sub: "weighted by amount"    },
              { label: "Longest Tail",         value: `${kpis.longestTailMonths}mo`,             sub: "oldest payment lag"    },
            ].map(kpi => (
              <div key={kpi.label} className="rounded-xl px-4 py-3 border border-border/60"
                style={{ background: withAlpha(branding.colors.primary, 0.03) }}>
                <div className="text-[10px] text-muted-foreground mb-1">{kpi.label}</div>
                <div className="text-[18px] font-bold text-foreground tabular-nums leading-none">{kpi.value}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{kpi.sub}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="px-2 pt-4 pb-2">
        {loading ? (
          <div className="flex items-center justify-center h-72 text-sm text-muted-foreground">Loading…</div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={data} margin={{ top: 8, right: 20, left: 4, bottom: 0 }}
              onMouseLeave={() => setHoveredCohort(null)}>
              <defs>
                {cohorts.map((cohort, i) => {
                  const color = getCashCohortColor(i, cohorts.length);
                  return (
                    <linearGradient key={cohort.key} id={`ccc-${cohort.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={color} stopOpacity={i === 0 ? 0.95 : 0.85} />
                      <stop offset="100%" stopColor={color} stopOpacity={i === 0 ? 0.55 : 0.30} />
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={withAlpha(branding.colors.primary, 0.06)} vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v}`}
                tick={{ fontSize: 10, fill: "var(--muted-foreground)" }} axisLine={false} tickLine={false} width={44} />
              <Tooltip content={<CashCohortTooltip cohorts={cohorts} />}
                cursor={{ stroke: withAlpha(branding.colors.primary, 0.15), strokeWidth: 1 }} />
              {/* Render oldest cohorts first (they go on bottom), newest last (on top) */}
              {[...cohorts].reverse().map((cohort, ri) => {
                const i = cohorts.length - 1 - ri;
                const color = getCashCohortColor(i, cohorts.length);
                const isHovered = hoveredCohort === cohort.key;
                const opacity = hoveredCohort === null ? 1 : isHovered ? 1 : 0.2;
                return (
                  <Area key={cohort.key} type="monotone" dataKey={cohort.key} stackId="cash" name={cohort.key}
                    fill={`url(#ccc-${cohort.key})`}
                    stroke={i === 0 ? branding.colors.primary : withAlpha(color, 0.5)}
                    strokeWidth={i === 0 ? 1.5 : 0.5}
                    fillOpacity={opacity}
                    strokeOpacity={opacity}
                    style={{ transition: "fill-opacity 0.2s ease, stroke-opacity 0.2s ease" }}
                    onMouseEnter={() => setHoveredCohort(cohort.key)}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Legend — newest cohort first */}
      {cohorts.length > 0 && (
        <div className="px-6 pb-3 flex flex-wrap gap-x-4 gap-y-1.5 items-center">
          {cohorts.map((cohort, i) => (
            <button key={cohort.key}
              className="flex items-center gap-1.5 text-[10px] transition-opacity"
              style={{ opacity: hoveredCohort === null || hoveredCohort === cohort.key ? 1 : 0.35 }}
              onMouseEnter={() => setHoveredCohort(cohort.key)}
              onMouseLeave={() => setHoveredCohort(null)}>
              <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: getCashCohortColor(i, cohorts.length) }} />
              <span className={i === 0 ? "text-foreground font-medium" : "text-muted-foreground"}>{cohort.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Cohort Matrix Table */}
      {granularity === "month" && cohortTable.length > 0 && (
        <div className="px-6 pb-6">
          <div className="text-[11px] font-semibold text-foreground mb-2">Revenue by cohort by month</div>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-[10px] tabular-nums border-collapse">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="text-left px-3 py-2 font-semibold text-muted-foreground sticky left-0 bg-card/95 whitespace-nowrap">Cohort month</th>
                  {Array.from({ length: maxOffset + 1 }, (_, i) => (
                    <th key={i} className="px-2 py-2 font-semibold text-muted-foreground text-right whitespace-nowrap">{i}</th>
                  ))}
                  <th className="px-3 py-2 font-semibold text-muted-foreground text-right border-l border-border whitespace-nowrap">Total</th>
                </tr>
              </thead>
              <tbody>
                {[...cohortTable].reverse().map((row, ri) => (
                  <tr key={row.cohort} className={`border-b border-border/40 ${ri % 2 === 0 ? "" : "bg-muted/10"}`}>
                    <td className="px-3 py-1.5 font-medium text-foreground sticky left-0 bg-card/95 whitespace-nowrap">{row.label}</td>
                    {Array.from({ length: maxOffset + 1 }, (_, i) => {
                      const v = row.offsets[i] ?? 0;
                      return (
                        <td key={i} className="px-2 py-1.5 text-right text-muted-foreground">
                          {v > 0 ? (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${Math.round(v)}`) : "—"}
                        </td>
                      );
                    })}
                    <td className="px-3 py-1.5 text-right font-semibold text-foreground border-l border-border">
                      {row.total >= 1000 ? `$${(row.total / 1000).toFixed(0)}k` : `$${Math.round(row.total)}`}
                    </td>
                  </tr>
                ))}
                {/* Column totals row */}
                <tr className="border-t border-border bg-muted/20 font-semibold">
                  <td className="px-3 py-2 text-foreground sticky left-0 bg-card/95">Total</td>
                  {Array.from({ length: maxOffset + 1 }, (_, i) => {
                    const colTotal = cohortTable.reduce((s, r) => s + (r.offsets[i] ?? 0), 0);
                    return (
                      <td key={i} className="px-2 py-2 text-right text-foreground">
                        {colTotal > 0 ? (colTotal >= 1000 ? `$${(colTotal / 1000).toFixed(0)}k` : `$${Math.round(colTotal)}`) : "—"}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right text-foreground border-l border-border">
                    {fmt(cohortTable.reduce((s, r) => s + r.total, 0))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </motion.div>
  );
}

function RevenueOriginCohortChart({ filters = {} }: { filters?: BuyingCycleFilters }) {
  const [data,         setData]         = useState<RevenueCohortPoint[]>([]);
  const [appData,      setAppData]      = useState<RevenueCohortPoint[]>([]);
  const [kpis,         setKpis]         = useState<RevenueCohortKPIs | null>(null);
  const [excludedCloses, setExcludedCloses] = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [granularity,  setGranularity]  = useState<"month" | "week">("month");
  const [metric,       setMetric]       = useState<"revenue" | "applications">("revenue");
  const [hoveredKey,   setHoveredKey]   = useState<CohortKey | null>(null);
  const [countMode,    setCountMode]    = useState<"people" | "events">("events");

  const COHORT_COLORS        = useMemo(() => buildCohortColors(), []);
  const COHORT_STROKE_COLORS = useMemo(() => buildCohortStrokeColors(), []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("granularity", granularity);
    if (filters.startDate) params.set("startDate", filters.startDate);
    if (filters.endDate)   params.set("endDate",   filters.endDate);
    if (filters.source)    params.set("source",    filters.source);
    if (metric === "applications") params.set("countMode", countMode);
    fetch(`/api/buying-cycle/revenue-cohort?${params}`)
      .then(r => r.json() as Promise<{ data: RevenueCohortPoint[]; appData: RevenueCohortPoint[]; kpis: RevenueCohortKPIs | null; excludedCloses: number }>)
      .then(d => { setData(d.data ?? []); setAppData(d.appData ?? []); setKpis(d.kpis ?? null); setExcludedCloses(d.excludedCloses ?? 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [granularity, metric, countMode, filters.startDate, filters.endDate, filters.source]);

  const activeData = metric === "revenue" ? data : appData;

  const handleMouseEnterLegend = useCallback((key: CohortKey) => setHoveredKey(key), []);
  const handleMouseLeaveLegend = useCallback(() => setHoveredKey(null), []);

  const getAreaOpacity = (key: CohortKey) => {
    if (!hoveredKey) return 1;
    return hoveredKey === key ? 1 : 0.15;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="rounded-2xl border border-border bg-card overflow-hidden"
      style={{ boxShadow: "0 2px 24px rgba(0,0,0,0.06)" }}
    >
      {/* ── Header ── */}
      <div className="px-6 pt-6 pb-0">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-[13px] font-semibold text-foreground tracking-tight">Revenue Origin Cohort</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Optin → Close · Revenue and closes bucketed by how long leads took to convert
              {excludedCloses > 0 && (
                <span className="ml-2 text-[10px] opacity-50">· {excludedCloses} closes excluded (no optin record)</span>
              )}
            </p>
          </div>
          {/* Metric + Granularity toggles */}
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted/30 border border-border/50">
              {(["revenue", "applications"] as const).map(m => (
                <button key={m} onClick={() => setMetric(m)}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all duration-200 ${
                    metric === m ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}>
                  {m === "revenue" ? "Revenue" : "Applications"}
                </button>
              ))}
            </div>
            {metric === "applications" && (
              <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted/30 border border-border/50">
                {(["people", "events"] as const).map(cm => (
                  <button key={cm} onClick={() => setCountMode(cm)}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all duration-200 ${
                      countMode === cm ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                    }`}>
                    {cm === "people" ? "People Closed" : "Payment Events"}
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted/30 border border-border/50">
              {(["month", "week"] as const).map(g => (
                <button key={g} onClick={() => setGranularity(g)}
                  className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all duration-200 ${
                    granularity === g ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                  }`}>
                  {g === "month" ? "Monthly" : "Weekly"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── KPI Strip ── */}
        {kpis && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
            {[
              { label: "Median Days to Close",  value: `${kpis.medianDaysToClose}d`,     sub: "optin → close" },
              { label: "Oldest Conversion",     value: `${kpis.oldestConvertingDays}d`,  sub: "longest cycle" },
              { label: "Fastest Source",        value: kpis.fastestSource || "—",        sub: kpis.fastestSourceDays > 0 ? `${kpis.fastestSourceDays}d median` : "median days" },
            ].map(kpi => (
              <div key={kpi.label}
                className="rounded-xl px-4 py-3 border border-border/60"
                style={{ background: withAlpha(branding.colors.primary, 0.03) }}
              >
                <div className="text-[10px] text-muted-foreground font-medium mb-1 leading-tight">{kpi.label}</div>
                <div className="text-[20px] font-bold text-foreground tabular-nums leading-none tracking-tight">{kpi.value}</div>
                <div className="text-[9px] text-muted-foreground/60 mt-1">{kpi.sub}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Chart ── */}
      <div className="px-2 pt-5 pb-4">
        {loading ? (
          <div className="h-[280px] flex items-center justify-center">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full bg-[var(--brand-primary)] animate-pulse" />
              Loading cohort data…
            </div>
          </div>
        ) : activeData.length === 0 ? (
          <div className="h-[280px] flex items-center justify-center text-[11px] text-muted-foreground">No data available</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={activeData} margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
              onMouseLeave={() => setHoveredKey(null)}>
              <defs>
                {COHORT_KEYS.map((key) => (
                  <linearGradient key={key} id={`rcg-${key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={COHORT_COLORS[key]} stopOpacity={1} />
                    <stop offset="100%" stopColor={COHORT_COLORS[key]} stopOpacity={0.55} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.04} vertical={false} />
              <XAxis dataKey="period"
                tick={{ fontSize: 10, fill: "var(--muted-foreground)", fontFamily: "inherit" }}
                tickLine={false} axisLine={false}
                interval={granularity === "month" ? 1 : Math.floor(activeData.length / 8)}
              />
              <YAxis
                tick={{ fontSize: 10, fill: "var(--muted-foreground)", fontFamily: "inherit" }}
                tickLine={false} axisLine={false}
                tickFormatter={v => metric === "revenue" ? fmtRevenue(Number(v)) : String(Math.round(Number(v)))}
                width={48}
              />
              <Tooltip
                content={<RevenueCohortTooltip colors={COHORT_COLORS} isApplications={metric === "applications"} />}
                cursor={{ stroke: withAlpha(branding.colors.primary, 0.12), strokeWidth: 1 }}
              />
              {COHORT_KEYS.map(key => (
                <Area key={key} type="monotone" dataKey={key} stackId="1"
                  fill={`url(#rcg-${key})`}
                  stroke={COHORT_STROKE_COLORS[key]}
                  strokeWidth={0.8}
                  fillOpacity={getAreaOpacity(key)}
                  strokeOpacity={getAreaOpacity(key)}
                  activeDot={{ r: 3, fill: COHORT_STROKE_COLORS[key], stroke: "var(--background)", strokeWidth: 2 }}
                  style={{ transition: "fill-opacity 0.25s ease, stroke-opacity 0.25s ease" }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Legend ── */}
      <div className="flex items-center gap-x-4 gap-y-2 flex-wrap px-6 pb-5">
        {[...COHORT_KEYS].reverse().map(key => (
          <button key={key}
            className="flex items-center gap-1.5 group"
            onMouseEnter={() => handleMouseEnterLegend(key)}
            onMouseLeave={handleMouseLeaveLegend}
            style={{ opacity: hoveredKey && hoveredKey !== key ? 0.4 : 1, transition: "opacity 0.2s ease" }}
          >
            <div className="w-2.5 h-2.5 rounded-sm shrink-0 transition-transform group-hover:scale-125"
              style={{ background: COHORT_COLORS[key], border: `1px solid ${COHORT_STROKE_COLORS[key]}` }} />
            <span className="text-[10px] text-muted-foreground font-medium">{COHORT_LABELS[key]}</span>
          </button>
        ))}
        <div className="ml-auto text-[10px] text-muted-foreground/50 italic">
          Hover cohort to highlight
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────
export function BuyingCycle() {
  const [tab, setTab]         = useState<"analytics" | "lookup">("analytics");
  const [filters, setFilters] = useState<BuyingCycleFilters>({});
  const [options, setOptions] = useState<FilterOptions>({
    sources: [], funnels: [], contents: [], countries: [],
    closers: [], setters: [], responseTypes: [], leadTypes: [],
  });

  useEffect(() => {
    fetch("/api/buying-cycle/filter-options")
      .then(r => r.json() as Promise<FilterOptions>)
      .then(setOptions)
      .catch(() => {/* non-critical */});
  }, []);

  const { analytics, loading, error, total } = useLeadHistoryAnalytics(filters);

  function setFilter(key: keyof BuyingCycleFilters, value: string) {
    setFilters(prev => ({ ...prev, [key]: value || undefined }));
  }

  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="space-y-8 w-full max-w-full overflow-x-hidden">
      {/* ── Header ── */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center py-8"
      >
        <h1 className="text-4xl font-black tracking-tight mb-2">
          <span className="bg-linear-to-r from-foreground via-foreground/80 to-foreground/60 bg-clip-text text-transparent">
            BUYING CYCLE
          </span>
        </h1>
        <p className="text-sm text-muted-foreground">
          Cohort analysis, lead timing, and sales cycle quality
        </p>
      </motion.div>

      {/* ── Tabs ── */}
      <div className="flex gap-1 p-1 rounded-xl bg-muted/30 border border-border w-fit">
        {([["analytics", "Cohort Analytics"], ["lookup", "Optin Lookup"]] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              tab === id
                ? "bg-[var(--brand-primary)] text-white shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="space-y-8"
      >
        {tab === "lookup" && (
          <div className="rounded-xl border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-foreground mb-1">Optin Lookup</h2>
            <p className="text-xs text-muted-foreground mb-4">Search by name or email to see full attribution, lifecycle, and every call on record.</p>
            <OptinLookup />
          </div>
        )}

        {tab === "analytics" && <>
        {/* ── Filter bar ── */}
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Filters</span>
            {activeCount > 0 && (
              <button
                onClick={() => setFilters({})}
                className="text-[11px] text-[var(--brand-primary)] hover:underline"
              >
                Clear all ({activeCount})
              </button>
            )}
          </div>
          {/* Year quick-select */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Year</span>
            {YEAR_OPTIONS.map(y => {
              const active = (filters.startDate ?? "") === y.start && (filters.endDate ?? "") === y.end;
              return (
                <button
                  key={y.label}
                  onClick={() => setFilters(prev => ({ ...prev, startDate: y.start || undefined, endDate: y.end || undefined }))}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
                    active
                      ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
                      : "bg-background text-muted-foreground border-border hover:border-[var(--brand-primary)]/50"
                  }`}
                >
                  {y.label}
                </button>
              );
            })}
          </div>
          {/* Custom date range */}
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">From</label>
              <input
                type="date"
                value={filters.startDate ?? ""}
                onChange={e => setFilter("startDate", e.target.value)}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]"
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">To</label>
              <input
                type="date"
                value={filters.endDate ?? ""}
                onChange={e => setFilter("endDate", e.target.value)}
                className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <FilterSelect label="Source"        value={filters.source       ?? ""} options={options.sources}       onChange={v => setFilter("source",       v)} />
            <FilterSelect label="Funnel"        value={filters.funnel       ?? ""} options={options.funnels}       onChange={v => setFilter("funnel",       v)} />
            <FilterSelect label="UTM Content"   value={filters.utmContent   ?? ""} options={options.contents}      onChange={v => setFilter("utmContent",   v)} />
            <FilterSelect label="Lead Type"     value={filters.leadType     ?? ""} options={options.leadTypes}     onChange={v => setFilter("leadType",     v)} />
            <FilterSelect label="Response Type" value={filters.responseType ?? ""} options={options.responseTypes} onChange={v => setFilter("responseType", v)} />
            <FilterSelect label="Country"       value={filters.country      ?? ""} options={options.countries}     onChange={v => setFilter("country",      v)} />
            <FilterSelect label="Closer"        value={filters.closer       ?? ""} options={options.closers}       onChange={v => setFilter("closer",       v)} />
            <FilterSelect label="Setter"        value={filters.setter       ?? ""} options={options.setters}       onChange={v => setFilter("setter",       v)} />
          </div>
        </div>

        {/* ── Data source badge ── */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--brand-primary)]/10 border border-[var(--brand-primary)]/20 text-[10px] font-semibold text-[var(--brand-primary)] uppercase tracking-wider">
            <span className={`w-1.5 h-1.5 rounded-full ${loading ? "bg-yellow-400 animate-pulse" : "bg-green-400"}`} />
            {loading ? "Loading…" : `${total} optins · Live Data`}
          </span>
          {activeCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-[10px] font-semibold text-amber-400">
              {activeCount} filter{activeCount !== 1 ? "s" : ""} active
            </span>
          )}
          {error && (
            <span className="text-[10px] text-red-400">{error}</span>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
            Loading buying cycle data…
          </div>
        )}

        <BuyingCycleDistributionChart filters={filters} />

        <OptinApplicationDistributionChart filters={filters} />

        <OptinAgeChart filters={filters} />

        <RevenueOriginCohortChart filters={filters} />

        <CashCollectionCohortChart filters={filters} />

        <OptinAppFunnelChart filters={filters} />

        {!loading && !error && analytics && (
          <LeadHistoryCohortSection analytics={analytics} />
        )}

        </>}

      </motion.div>
    </div>
  );
}
